import { ID } from '../types/common';
import { Prompt } from '../models/prompt';
import { IPromptRepository } from '../repositories/promptRepository';
import { IFolderRepository } from '../repositories/folderRepository';
import { DomainEventPublisher, DomainEventFactory } from '../events/domainEvents';
import { DomainError } from '../errors/domainError';
import { ErrorCode } from '../errors/errorCodes';
// Tombstone import removed to prevent folders from being randomly deleted

export interface CreatePromptDTO {
  title: string;
  text: string;
  description: string;
  tags: string[];
  folderId: ID;
}

export interface UpdatePromptDTO extends Partial<CreatePromptDTO> {}

export class PromptService {
  constructor(
    private readonly promptRepository: IPromptRepository,
    private readonly folderRepository: IFolderRepository,
    private readonly eventPublisher: DomainEventPublisher
  ) {}

  async getPrompt(id: ID): Promise<Prompt | null> {
    return this.promptRepository.findById(id);
  }

  async createPrompt(dto: CreatePromptDTO, userId?: ID): Promise<Prompt> {
    // Validate folder exists
    const folder = await this.folderRepository.findById(dto.folderId);
    if (!folder) {
      throw new DomainError(ErrorCode.FOLDER_NOT_FOUND, `Folder ${dto.folderId} not found`);
    }

    // Create prompt
    const prompt = Prompt.create({
      ...dto,
      createdBy: userId,
      updatedBy: userId,
    });

    // Save to repository
    const savedPrompt = await this.promptRepository.create(prompt);

    // Publish event
    await this.eventPublisher.publish(DomainEventFactory.promptCreated(savedPrompt, userId));

    return savedPrompt;
  }

  async updatePrompt(id: ID, changes: UpdatePromptDTO, userId?: ID): Promise<Prompt> {
    // Get existing prompt
    const existingPrompt = await this.promptRepository.findById(id);
    if (!existingPrompt) {
      throw new DomainError(ErrorCode.PROMPT_NOT_FOUND, `Prompt ${id} not found`);
    }

    // If folder is being changed, validate new folder exists
    if (changes.folderId && changes.folderId !== existingPrompt.folderId) {
      const folder = await this.folderRepository.findById(changes.folderId);
      if (!folder) {
        throw new DomainError(ErrorCode.FOLDER_NOT_FOUND, `Folder ${changes.folderId} not found`);
      }
    }

    // Update prompt
    const updatedPrompt = existingPrompt.update({
      ...changes,
      updatedBy: userId,
    });

    // Save to repository
    const savedPrompt = await this.promptRepository.update(id, updatedPrompt);

    // Publish event
    await this.eventPublisher.publish(
      DomainEventFactory.promptUpdated(id, changes, existingPrompt, userId)
    );

    return savedPrompt;
  }
  async deletePrompt(id: ID, userId?: ID): Promise<void> {
    console.log('DELETION FLOW: Domain Step 1 - PromptService.deletePrompt called for prompt:', id);

    const prompt = await this.promptRepository.findById(id);
    if (!prompt) {
      console.log('DELETION FLOW: Domain Step 1.1 - Prompt not found, throwing error');
      throw new DomainError(ErrorCode.PROMPT_NOT_FOUND, `Prompt ${id} not found`);
    }
    console.log('DELETION FLOW: Domain Step 1.2 - Found prompt to delete:', {
      id: prompt.id,
      title: prompt.title,
      folderId: prompt.folderId,
    });

    // Tombstone creation removed to prevent folders from being randomly deleted
    console.log(
      'DELETION FLOW: Domain Step 1.3 - Tombstone creation skipped to prevent random deletions'
    );

    console.log('DELETION FLOW: Domain Step 2 - Calling promptRepository.delete');
    console.log('DELETION FLOW: Domain Step 2 - Calling promptRepository.delete');
    await this.promptRepository.delete(id);
    console.log('DELETION FLOW: Domain Step 2.1 - Repository delete operation completed');

    console.log('DELETION FLOW: Domain Step 3 - Publishing promptDeleted event');
    await this.eventPublisher.publish(
      DomainEventFactory.promptDeleted(id, prompt.folderId, userId)
    );
    console.log('DELETION FLOW: Domain Step 3.1 - Event published successfully');

    console.log('DELETION FLOW: Domain Step 4 - PromptService.deletePrompt completed successfully');
  }

  async movePrompt(id: ID, targetFolderId: ID, userId?: ID): Promise<Prompt> {
    // Validate prompt exists
    const prompt = await this.promptRepository.findById(id);
    if (!prompt) {
      throw new DomainError(ErrorCode.PROMPT_NOT_FOUND, `Prompt ${id} not found`);
    }

    // Validate target folder exists
    const targetFolder = await this.folderRepository.findById(targetFolderId);
    if (!targetFolder) {
      throw new DomainError(ErrorCode.FOLDER_NOT_FOUND, `Folder ${targetFolderId} not found`);
    }

    // Move prompt
    const movedPrompt = await this.promptRepository.moveToFolder(id, targetFolderId);

    // Publish event
    await this.eventPublisher.publish(
      DomainEventFactory.promptMoved(id, prompt.folderId, targetFolderId, userId)
    );

    return movedPrompt;
  }

  async bulkMovePrompts(promptIds: ID[], targetFolderId: ID, userId?: ID): Promise<void> {
    // Validate target folder exists
    const targetFolder = await this.folderRepository.findById(targetFolderId);
    if (!targetFolder) {
      throw new DomainError(ErrorCode.FOLDER_NOT_FOUND, `Folder ${targetFolderId} not found`);
    }

    // Capture each prompt's source folder BEFORE moving. Re-fetching after the
    // move returns targetFolderId for both from and to, so the previous code
    // published promptMoved events whose fromFolderId == toFolderId (#112).
    const sourceFolderById = new Map<ID, ID>();
    for (const promptId of promptIds) {
      const prompt = await this.promptRepository.findById(promptId);
      if (prompt) {
        sourceFolderById.set(promptId, prompt.folderId);
      }
    }

    // Move prompts
    await this.promptRepository.bulkMove(promptIds, targetFolderId);

    // Publish events with the real source folder captured above.
    for (const [promptId, sourceFolderId] of sourceFolderById) {
      await this.eventPublisher.publish(
        DomainEventFactory.promptMoved(promptId, sourceFolderId, targetFolderId, userId)
      );
    }
  }

  async addTags(id: ID, tags: string[], userId?: ID): Promise<Prompt> {
    const prompt = await this.promptRepository.findById(id);
    if (!prompt) {
      throw new DomainError(ErrorCode.PROMPT_NOT_FOUND, `Prompt ${id} not found`);
    }

    const updatedPrompt = await this.promptRepository.addTags(id, tags);

    await this.eventPublisher.publish(
      DomainEventFactory.promptUpdated(id, { tags: updatedPrompt.tags }, prompt, userId)
    );

    return updatedPrompt;
  }

  async removeTags(id: ID, tags: string[], userId?: ID): Promise<Prompt> {
    const prompt = await this.promptRepository.findById(id);
    if (!prompt) {
      throw new DomainError(ErrorCode.PROMPT_NOT_FOUND, `Prompt ${id} not found`);
    }

    const updatedPrompt = await this.promptRepository.removeTags(id, tags);

    await this.eventPublisher.publish(
      DomainEventFactory.promptUpdated(id, { tags: updatedPrompt.tags }, prompt, userId)
    );

    return updatedPrompt;
  }

  async markAsUsed(id: ID): Promise<void> {
    const prompt = await this.promptRepository.findById(id);
    if (!prompt) {
      throw new DomainError(ErrorCode.PROMPT_NOT_FOUND, `Prompt ${id} not found`);
    }

    await this.promptRepository.markAsUsed(id);
  }

  async toggleFavorite(id: ID): Promise<void> {
    const prompt = await this.promptRepository.findById(id);
    if (!prompt) {
      throw new DomainError(ErrorCode.PROMPT_NOT_FOUND, `Prompt ${id} not found`);
    }

    await this.promptRepository.toggleFavorite(id);
  }

  async exportPrompts(promptIds?: ID[]): Promise<string> {
    return this.promptRepository.exportPrompts(promptIds);
  }

  async importPrompts(data: string): Promise<{
    imported: number;
    skipped: number;
    errors: Array<{ id: string; error: string }>;
  }> {
    return this.promptRepository.importPrompts(data);
  }

  async getPromptCount(folderId: ID): Promise<number> {
    return this.promptRepository.countByFolder(folderId);
  }

  async getPrompts(folderId?: ID): Promise<Prompt[]> {
    if (folderId) {
      const result = await this.promptRepository.findByFolder(folderId);
      return result.data ?? [];
    }
    return this.promptRepository.findAll();
  }
}
